import styled from 'styled-components';
import { SongSlug } from '../../../common/src/model.ts';
import { decodeSongSlug, isSongSlug } from '../../../common/src/paths.ts';
import { userVisibleSongNumber } from '../../../common/src/util.ts';
import BackArrowIcon from '../assets/arrow-previous-left-icon.svg?react';
import CheckMarkIcon from '../assets/check-mark-icon.svg?react';
import ChevronDownIcon from '../assets/chevron-down.svg?react';
import CloseIcon from '../assets/close.svg?react';
import PencilIcon from '../assets/pencil.svg?react';
import QuarterNoteIcon from '../assets/quarter-note.svg?react';
import { localStorageGet, LocalStorageKey } from '../data/client_local_storage.ts';
import { HymnalWithHymns } from '../data/hymnals.ts';
import { isInsideAppleAppStoreApp } from './billing.ts';
import { getSongSlugForHymn } from './path.ts';

export function isHymnUrl(search?: string): boolean {
  const searchParams = new URLSearchParams(search);
  const songSlug = searchParams.get('songSlug');
  if (songSlug && isSongSlug(songSlug)) {
    return true;
  }
  // backward compatible song URL parsing
  const hymnTitle = searchParams.get('title');
  const hymnNumber = Number(searchParams.get('number'));
  const hymnalName = searchParams.get('hymnal');
  return !!hymnTitle && !!hymnNumber && !!hymnalName;
}

export const songSlugSearchParamName = 'songSlug';

export function getSongSlugFromUrl(hymnals: HymnalWithHymns[], search: string | undefined): SongSlug | undefined {
  if (!search) {
    return;
  }
  const searchParams = new URLSearchParams(search);
  const songSlug = searchParams.get(songSlugSearchParamName);
  if (songSlug && isSongSlug(songSlug)) {
    return decodeSongSlug(songSlug);
  }

  if (hymnals.length === 0) {
    return;
  }

  // Backward compatible URL parsing.  For example:
  // https://singyourpart.app/this-week?hymnal=Treasury_of_Psalms_and_Hymns&title=Let_God_Arise_in_All_His_Might_-_Psalm_68&number=211&psalm=
  const hymnTitle = searchParams.get('title');
  const songNumberRaw = searchParams.get('number');
  const hymnalName = searchParams.get('hymnal');
  const basePath = searchParams.get('basePath') as string | undefined;

  if (!hymnTitle || !songNumberRaw || !hymnalName) {
    return;
  }

  // for backwards compatibility with song URLs that contain a decimal
  const songNumber = songNumberRaw.match(/^\d+\.\d+$/) ? userVisibleSongNumber(Number(songNumberRaw)) : songNumberRaw;
  // in old URLs, spaces were previously replaced with underscores and years not wrapped in parentheses
  const correctedHymnalName = hymnalName
    .replace(/_/g, ' ')
    .replace(/(?<!\()\b2002\b(?!\))/g, '(2002)')
    .replace(/(?<!\()\b2020\b(?!\))/g, '(2020)');
  return getSongSlugForHymn(hymnals, { songNumber, hymnal: correctedHymnalName, basePath });
}

// NOTE: Only use if no keyboard!
export const OuterFrame = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  color: var(--color-text);
  background-color: var(--color-background);
`;

// deprecated - prefer OuterFrame
export const OuterPageContent = styled.div<{ $noHeight?: boolean }>`
  display: flex;
  flex-flow: column;
  color: var(--color-text);

  // Screen Height
  // -------------
  // - WkWebView appears to be being resized by the soft keyboard
  // - This causes 100lvh to be incorrectly squeezed and causes 3/4 JavaScript window height metrics to be squeezed:
  //   - Squeezed: window,innerHeight, window.outerHeight, window.visualViewport.height
  //   - Unsqueezed: window.screen.height (entire screen, including unsafe areas above/below
  // - Thus, there is really no way for the web app to reliably get a useful keyboard-agnostic screen height
  // - It may be possible for the native app to avoid squeezing the WkWebView, but then we may not know keyboard height
  // - Alternatively, we could make use of the unsafe areas as well (native change), making window.screen.height useful
  // - For now, we use svh in the browser to account for the address bar
  height: ${(props) => {
    return props.$noHeight ? 'auto' : isInsideAppleAppStoreApp() ? '100vh' : 'var(--view-height, 100svh)';
  }};
`;

export const InnerPageContent = styled.div`
  flex: auto;
  overflow-y: auto;
  white-space: nowrap;
  max-width: 100vw;
  isolation: isolate;
`;

export const SettingsTransitionWrapper = styled(OuterPageContent)<{ $left?: string }>`
  position: fixed;
  left: ${(props) => props.$left ?? 0};
  width: 100%;
  top: 0;
  bottom: 0;
  transition: left 200ms;
  transition-timing-function: ease-out;
`;

export const PlainLinkOutlined = styled.a<{ $small?: boolean }>`
  margin-top: 10px;
  padding: 5px 10px;
  border: 3px solid var(--color-text);
  border-radius: 15px;
  cursor: pointer;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: ${(props) => (props.$small ? '1em' : '1.3em')};
  text-decoration: none;
  width: fit-content;

  &:hover {
    background-color: lightgray;
  }
`;

export const ContactSupportLink = ({ capitalized }: { capitalized?: boolean }) => (
  <a href="mailto:support@crescendosw.com">{capitalized ? 'C' : 'c'}ontact support</a>
);

export const FloatingBackButton = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 36px;
  z-index: 1000;
  // TODO(hewitt): properly set back button color once PDF is displayed white on black for dark mode
  // background-color: color-mix(in srgb, var(--color-background) 80%, transparent);
  // color: var(--color-text);
  background-color: rgba(255, 255, 255, 0.8);
  margin: 2px 8px;
  padding: 15px 10px;
  border-radius: 3px;
  cursor: pointer;
`;

// see also CheckBox
export const CheckMark = styled(CheckMarkIcon)<{
  $size?: string;
  $padding?: string;
  $fillColor?: string;
  $noAutoMargin?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(props) => props.$size ?? '35px'};
  height: ${(props) => props.$size ?? '35px'};
  padding-right: ${(props) => props.$padding ?? '15px'};
  ${(props) => !props.$noAutoMargin && 'margin-left: auto'};
  fill: ${(props) => props.$fillColor ?? 'var(--color-text)'};
`;

export const BackButtonWrapper = styled.div`
  display: flex;
  justify-content: start;
  padding-left: 15px;
  padding-right: 10px;
`;

export const BackArrow = styled(BackArrowIcon)`
  align-items: center;
  height: 24px;
  cursor: pointer;
  fill: var(--color-text);
  flex-shrink: 0;
`;

export const DownArrow = styled(ChevronDownIcon)<{ $color?: string }>`
  align-items: center;
  height: 20px;
  width: 20px;
  cursor: pointer;
  fill: ${(props) => props.$color ?? 'var(--color-text)'};
  flex-shrink: 0;
`;

export function QuarterNote({ hidden, width }: { hidden?: boolean; width?: string }) {
  return (
    <QuarterNoteWrapper hidden={hidden} $width={width}>
      <QuarterNoteIcon />
    </QuarterNoteWrapper>
  );
}

const QuarterNoteWrapper = styled.div<{ $width?: string }>`
  flex-shrink: 0;
  fill: var(--color-text);
  width: ${(props) => (props.$width ? props.$width : '12px')};
  margin-right: 3px;
`;

// from https://stackoverflow.com/a/29883167/998490
export function focusNextTabStop(element: HTMLInputElement): void {
  const universe = document.querySelectorAll('input, button, select, textarea, a[href]');
  const list = Array.prototype.filter.call(universe, function (item) {
    return item.tabIndex >= '0';
  });
  const index = list.indexOf(element);
  const nextElement = list[index + 1] || list[0];
  nextElement?.focus();
}

export function userIsSignedIn() {
  return Boolean(localStorageGet(LocalStorageKey.ActiveUserToken));
}

export const ContactSupportButton = ({ small }: { small?: boolean }) => {
  return (
    <PlainLinkOutlined href="mailto:support@crescendosw.com" $small={small}>
      Contact Support
    </PlainLinkOutlined>
  );
};

export function CloseButton({
  onClick,
  invertColors,
  isCloseToTop,
  isFarFromRight,
  isSmall,
}: {
  onClick: () => void;
  invertColors?: boolean;
  isCloseToTop?: boolean;
  isFarFromRight?: boolean;
  isSmall?: boolean;
}) {
  return (
    <CloseButtonWrapper
      onClick={onClick}
      $isCloseToTop={isCloseToTop}
      $isFarFromRight={isFarFromRight}
      $invertColors={invertColors}
      $isSmall={isSmall}
    >
      <CloseIcon />
    </CloseButtonWrapper>
  );
}

const CloseButtonWrapper = styled.button<{
  $isCloseToTop?: boolean;
  $isFarFromRight?: boolean;
  $invertColors?: boolean;
  $isSmall?: boolean;
}>`
  z-index: 1; // TODO(hewitt): should not be necessary - wasn't when in frame.ts
  fill: ${(props) => (props.$invertColors ? 'var(--color-background)' : 'var(--color-text-light)')};
  width: ${(props) => (props.$isSmall ? '35px' : '50px')};
  position: absolute;
  top: ${(props) => (props.$isCloseToTop ? '5px' : '15px')};
  right: ${(props) => (props.$isFarFromRight ? '15px' : '5px')};
  background-color: ${(props) => (props.$invertColors ? 'var(--color-text)' : 'transparent')};
  border: none;
  cursor: pointer;
  border-radius: 50px;
`;

export const headerHeight = '55px';

const headerFontSize = '1.17rem';

export const Title = styled.div<{ $leftMargin?: string; $textAlign?: string; $lineClamp?: number }>`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: ${headerFontSize};

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${(props) => (props.$lineClamp !== undefined ? props.$lineClamp : 2)};
  ${(props) => (props.$textAlign ? `text-align: ${props.$textAlign}` : '')};
  ${(props) => (props.$leftMargin ? `margin-left: ${props.$leftMargin}` : '')};
  // Note "overflow: hidden" would prevent the org page popup menu from appearing, so localized that to SongTitle
`;

export const RedDotIcon = styled.div`
  position: absolute;
  background-color: red;
  width: 13px;
  height: 13px;
  border-radius: 13px;
  top: 1px;
  right: 0;
`;

export const hymnalIconWidth = '4em';
export const hymnalEntryPadding = '0.3rem';

export const HymnalIconContainer = styled.div`
  width: ${hymnalIconWidth};
  padding: ${hymnalEntryPadding};
  flex-shrink: 0;
  position: relative;
`;

export const HymnalIcon = styled.img<{ $hasBorder: boolean; $hasWash: boolean }>`
  vertical-align: middle;
  border-radius: 0.5em;
  border: 1px solid transparent;
  ${(props) => props.$hasBorder && `border: 1px solid;`}
  ${(props) => props.$hasWash && `opacity: 75%;`}
`;
export const Pencil = styled(PencilIcon)`
  width: 30px;
  height: 30px;
  padding-left: 10px;
  cursor: pointer;
  fill: currentColor; /* follows the surrounding text color, e.g. gold section headings */
`;

const colorResolver = document.createElement('div');
colorResolver.style.display = 'none';
document.body.appendChild(colorResolver);

// converts colors like "white" and "black" to hex values like "#000000" and "#ffffff"
// just returns the color if already hex
export function cssColorToHex(color: string): string | undefined {
  if (color.startsWith('#')) {
    return color;
  }
  colorResolver.style.color = color;

  const rgb = getComputedStyle(colorResolver).color;
  const match = rgb.match(/\d+/g);
  if (!match) {
    return;
  }
  const [r, g, b] = match.map(Number);

  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}
